import React, {useContext, useEffect, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {BillingStatus, TaskDocumentWithTask, TaskServiceUtils} from '../../../../services/TaskServiceUtils';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {Invoice, InvoiceEmitter, InvoiceListResponse, InvoiceService} from '../../../../services/invoiceService';
import EmitterSelectDialog from './EmitterSelectDialog';
import InvoiceCreateDialog, {TaskDataForInvoice} from './InvoiceCreateDialog';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// Format date for display
const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

// Format file size
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const RapporteTab: React.FC = () => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [documents, setDocuments] = useState<TaskDocumentWithTask[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dialog states for invoice creation
    const [selectedRapport, setSelectedRapport] = useState<TaskDocumentWithTask | null>(null);
    const [emitterSelectOpen, setEmitterSelectOpen] = useState(false);
    const [selectedEmitter, setSelectedEmitter] = useState<InvoiceEmitter | null>(null);
    const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
    const [taskDataForInvoice, setTaskDataForInvoice] = useState<TaskDataForInvoice | undefined>(undefined);

    // Billing status menu state
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [statusMenuDoc, setStatusMenuDoc] = useState<TaskDocumentWithTask | null>(null);

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = () => {
        setLoading(true);
        setError(null);
        call_backend_api(
            TaskServiceUtils.getAllDocuments('RAPPORT'),
            (data: TaskDocumentWithTask[]) => {
                setDocuments(data);
                setLoading(false);
            },
            (err: any) => {
                console.error('Error loading documents:', err);
                setError('Fehler beim Laden der Rapporte');
                setLoading(false);
            },
            undefined,
            false
        );
    };

    const handleOpenPdf = (doc: TaskDocumentWithTask) => {
        const baseUrl = import.meta.env.DEV
            ? 'http://localhost:8008'
            : import.meta.env.VITE_BACKEND_URL || '';
        const documentUrl = `${baseUrl}${doc.document_path}`;
        window.open(documentUrl, '_blank');
    };

    // Handle create invoice button click
    const handleCreateInvoice = (doc: TaskDocumentWithTask) => {
        setSelectedRapport(doc);

        // Fetch the full task data to get invoice contact and rapport fields
        call_backend_api(
            TasksService.getTaskTaskTaskIdGet(doc.task_id),
            (taskData: any) => {
                // Extract invoice-relevant data from task
                setTaskDataForInvoice({
                    invoice_name: taskData.invoice_name,
                    invoice_email: taskData.invoice_email,
                    invoice_phone: taskData.invoice_phone,
                    invoice_street: taskData.invoice_street,
                    invoice_additional_street: taskData.invoice_additional_street,
                    invoice_postal_code: taskData.invoice_postal_code,
                    invoice_city: taskData.invoice_city,
                    arbeitszeit: taskData.arbeitszeit,
                    anfahrt_abfahrt_mit_kfz: taskData.anfahrt_abfahrt_mit_kfz,
                    material: taskData.material,
                    title: taskData.title,
                    description: taskData.description,
                    rapport: taskData.rapport,
                    projekt_name: taskData.projekt_name,
                    workDate: doc.uploaded_at, // Use rapport upload date as work date
                    invoice_full_contact: taskData.invoice_full_contact,
                    invoice_use_full_contact: taskData.invoice_use_full_contact,
                });
                setEmitterSelectOpen(true);
            },
            (err: any) => {
                console.error('Error loading task data:', err);
                // Continue without task data
                setTaskDataForInvoice(undefined);
                setEmitterSelectOpen(true);
            },
            undefined,
            false
        );
    };

    // Handle emitter selection
    const handleEmitterSelect = (emitter: InvoiceEmitter) => {
        setSelectedEmitter(emitter);
        setEmitterSelectOpen(false);
        setInvoiceCreateOpen(true);
    };

    // Handle billing status chip click
    const handleStatusChipClick = (event: React.MouseEvent<HTMLElement>, doc: TaskDocumentWithTask) => {
        if (doc.billing_status !== 'BILLED') {
            setStatusMenuAnchor(event.currentTarget);
            setStatusMenuDoc(doc);
        }
    };

    const handleStatusMenuClose = () => {
        setStatusMenuAnchor(null);
        setStatusMenuDoc(null);
    };

    const handleStatusChange = (newStatus: BillingStatus) => {
        if (!statusMenuDoc) return;

        call_backend_api(
            TasksService.updateTaskTaskTaskIdPut(statusMenuDoc.task_id, { billing_status: newStatus } as any),
            () => {
                // Update local state
                setDocuments(docs =>
                    docs.map(d =>
                        d.id === statusMenuDoc.id ? { ...d, billing_status: newStatus } : d
                    )
                );
                handleStatusMenuClose();
            },
            (err: any) => {
                console.error('Error updating billing status:', err);
                handleStatusMenuClose();
            },
            undefined,
            false
        );
    };

    // Handle invoice dialog close
    const handleInvoiceClose = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
        setSelectedRapport(null);
        setTaskDataForInvoice(undefined);
    };

    // Handle invoice saved
    const handleInvoiceSave = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
        setSelectedRapport(null);
        setTaskDataForInvoice(undefined);
        // Reload documents to update billing status
        loadDocuments();
    };

    // Handle change emitter (go back to emitter selection)
    const handleChangeEmitter = () => {
        setInvoiceCreateOpen(false);
        setEmitterSelectOpen(true);
    };

    // Handle opening invoice PDF for a billed rapport
    const handleOpenInvoicePdf = (doc: TaskDocumentWithTask) => {
        call_backend_api(
            InvoiceService.getInvoices({limit: 1000}),
            (response: InvoiceListResponse) => {
                const invoice = response.items.find(inv => inv.task_id === doc.task_id);
                if (invoice?.pdf_path) {
                    const baseUrl = import.meta.env.DEV
                        ? 'http://localhost:8008'
                        : import.meta.env.VITE_BACKEND_URL || '';
                    window.open(`${baseUrl}${invoice.pdf_path}`, '_blank');
                } else {
                    console.warn('No invoice PDF found for task:', doc.task_id);
                }
            },
            (err: any) => {
                console.error('Error loading invoices:', err);
            },
            undefined,
            false
        );
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{mt: 2}}>
                {error}
            </Alert>
        );
    }

    if (documents.length === 0) {
        return (
            <Box sx={{py: 4, textAlign: 'center'}}>
                <DescriptionIcon sx={{fontSize: 60, color: 'text.secondary', mb: 2}}/>
                <Typography variant="body1" color="text.secondary">
                    Noch keine Rapporte vorhanden.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    Rapporte werden aus Aufgaben erstellt und hier angezeigt.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Dateiname</TableCell>
                            <TableCell>Aufgabe</TableCell>
                            <TableCell>Hochgeladen am</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">Größe</TableCell>
                            <TableCell width={150}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((doc) => (
                            <TableRow key={doc.id} hover>
                                <TableCell>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <DescriptionIcon fontSize="small" color="error"/>
                                        <Typography variant="body2">{doc.filename}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{doc.task_title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Aufgabe #{doc.task_id}
                                    </Typography>
                                </TableCell>
                                <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                                <TableCell align="center">
                                    {doc.billing_status === 'BILLED' ? (
                                        <Chip
                                            label="Abgerechnet"
                                            color="success"
                                            size="small"
                                            variant="outlined"
                                        />
                                    ) : doc.billing_status === 'CLOSED' ? (
                                        <Chip
                                            label="Abgeschlossen"
                                            color="primary"
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => handleStatusChipClick(e, doc)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ) : (
                                        <Chip
                                            label="Offen"
                                            color="warning"
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => handleStatusChipClick(e, doc)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="right">{formatFileSize(doc.file_size)}</TableCell>
                                <TableCell>
                                    <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end'}}>
                                        <Tooltip title="Rapport öffnen">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenPdf(doc)}
                                                color="primary"
                                            >
                                                <OpenInNewIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                        {doc.billing_status === 'OPEN' && (
                                            <Tooltip title="Rechnung erstellen">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<ReceiptIcon/>}
                                                    onClick={() => handleCreateInvoice(doc)}
                                                    sx={{ml: 1}}
                                                >
                                                    Rechnung
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {doc.billing_status === 'BILLED' && (
                                            <Tooltip title="Rechnung öffnen">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenInvoicePdf(doc)}
                                                    sx={{ml: 1}}>
                                                    <PictureAsPdfIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Emitter Selection Dialog */}
            <EmitterSelectDialog
                open={emitterSelectOpen}
                onClose={() => {
                    setEmitterSelectOpen(false);
                    setSelectedRapport(null);
                    setTaskDataForInvoice(undefined);
                }}
                onSelect={handleEmitterSelect}
            />

            {/* Invoice Create Dialog */}
            {selectedEmitter && (
                <InvoiceCreateDialog
                    open={invoiceCreateOpen}
                    emitter={selectedEmitter}
                    onClose={handleInvoiceClose}
                    onSave={handleInvoiceSave}
                    onChangeEmitter={handleChangeEmitter}
                    taskData={taskDataForInvoice}
                    taskId={selectedRapport?.task_id}
                />
            )}

            {/* Billing Status Menu */}
            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusMenuClose}
            >
                <MenuItem
                    onClick={() => handleStatusChange('OPEN')}
                    selected={statusMenuDoc?.billing_status === 'OPEN'}
                >
                    Offen
                </MenuItem>
                <MenuItem
                    onClick={() => handleStatusChange('CLOSED')}
                    selected={statusMenuDoc?.billing_status === 'CLOSED'}
                >
                    Abgeschlossen
                </MenuItem>
            </Menu>
        </>
    );
};

export default RapporteTab;
